import { TCurrencyCode } from 'interfaces';
import { EUserType } from 'services/BackendApi';

// ============================================
// ENUMS (List Page)
// ============================================

/**
 * Processing status for bank transactions.
 * Matches BE enum: BankTransactionProcessingStatus
 */
export enum EBankTransactionProcessingStatus {
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  READY_TO_PROCESS = 'READY_TO_PROCESS', // Displayed as "Ready To Process" in UI
  IN_PROGRESS = 'IN_PROGRESS',
  PROCESSED = 'PROCESSED',
}

/**
 * Transaction type derived from amount sign (not stored in BE).
 * Used for UI display only.
 */
export enum EBankTransactionType {
  CREDIT = 'CREDIT', // Positive amount - money in
  DEBIT = 'DEBIT', // Negative amount - money out
}

// ============================================
// ENUMS (Detail Page)
// ============================================

export enum ETransactionType {
  CREDIT = 'Credit',
  DEBIT = 'Debit',
}

export enum ECounterpartyType {
  TC = 'TC',
}

export enum EAllocationType {
  BOOKING = 'BOOKING',
  TC = 'TC',
}

export enum EProcessingStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
}

// ============================================
// INTERFACES
// ============================================

/**
 * Payment method group as returned by the API.
 * Used for bank/payment method dropdown filter.
 */
export interface IPaymentMethodGroup {
  code: string;
  name: string;
}

/**
 * Counterparty (travel company) as returned by the API.
 */
export interface ICounterparty {
  uuid: string;
  name: string;
  type: string; // e.g., "TC" for Travel Company
}

/**
 * Allocation entity type enum.
 * Matches BE: AllocationEntityType
 */
export type TAllocationEntityType = 'BOOKING' | 'TC';

/**
 * Allocation entity (e.g., booking or TC).
 * Matches BE: AllocationEntity
 */
export interface IAllocationEntity {
  type: TAllocationEntityType;
  uuid: string;
}

/**
 * Allocation for a bank transaction.
 * Matches BE: AmountAllocation
 */
export interface IAllocation {
  entity: IAllocationEntity;
  amountCents: number;
  description: string | null;
}

/**
 * User info for audit fields
 */
export interface IUserInfo {
  uuid: string;
  type: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Processing history item.
 * Matches BE: BankTransactionProcessingHistoryItem
 * Contains the snapshot of allocations at the time of processing.
 */
export interface IProcessingHistoryItem {
  counterparty: ICounterparty;
  exchangeCurrency: TCurrencyCode | null;
  exchangeRate: number | null;
  allocations: IAllocation[];
  processedBy: IUserInfo;
  processedAt: string;
}

/**
 * Bank transaction as returned by the API.
 * Based on BE type: BankTransaction
 * Note: Some fields are only populated in detail view (allocations, processingHistory, etc.)
 */
export interface IBankTransaction {
  uuid: string;

  transactionDate: string; // ISO date string - PRIMARY date for display
  valueDate: string; // ISO date string - secondary date (both supported)

  narration: string | null;
  reference: string | null;

  /**
   * Amount in minor units (cents).
   * POSITIVE = Credit (money in)
   * NEGATIVE = Debit (money out)
   */
  amountCents: number;

  /**
   * Payment method group (e.g., bank or payment type).
   * Contains code and display name.
   */
  paymentMethodGroup: IPaymentMethodGroup;

  currency: TCurrencyCode; // Uses existing TCurrencyCode from interfaces.ts

  processingStatus: EBankTransactionProcessingStatus;

  /**
   * Counterparty (e.g., travel company).
   * Can be null if not yet assigned.
   */
  counterparty: ICounterparty | null;

  /**
   * Exchange currency for foreign transactions.
   */
  exchangeCurrency: TCurrencyCode | null;

  /**
   * Exchange rate for foreign transactions.
   */
  exchangeRate: number | null;

  // Detail page fields (populated by detail endpoint, may not be present in list response)
  allocations: IAllocation[];
  processingHistory: IProcessingHistoryItem[];
  createdBy: IUserInfo;
  createdAt: string;
  updatedBy: IUserInfo;
  updatedAt: string;
}

/**
 * API request payload for searching bank transactions.
 */
export interface IBankTransactionsSearchRequest {
  filter?: {
    transactionDateFrom?: string;
    transactionDateTo?: string;
    paymentMethodGroupCode?: string;
    processingStatuses?: EBankTransactionProcessingStatus[];
    search?: string;
  };
  pagination: {
    offset: number;
    limit: number;
  };
  sort: {
    key: string;
    order: 'asc' | 'desc';
  };
}

/**
 * API response for paginated bank transactions list.
 * Matches actual BE response structure.
 */
export interface IBankTransactionsSearchResponse {
  count: number;
  pagination: {
    offset: number;
    limit: number;
  };
  bankTransactions: IBankTransaction[];
}

/**
 * Internal response format used by the hook for UI consumption.
 */
export interface IBankTransactionsListResponse {
  data: IBankTransaction[];
  meta: {
    total: number;
    page: number;
    perPage: number;
  };
}

// ============================================
// FILTER TYPES
// ============================================

/**
 * Transaction type filter values.
 * CR = Credit (amount >= 0), DR = Debit (amount < 0)
 */
export type TBankTransactionTypeFilter = 'CR' | 'DR';

/**
 * Filter state for bank transactions list.
 * Used for both URL params and API requests.
 */
export interface IBankTransactionFilters {
  startDate: string | null; // ISO date string or null
  endDate: string | null; // ISO date string or null
  paymentMethodGroupCode: string | null; // Payment method group code or null for "All"
  type: TBankTransactionTypeFilter | null; // CR=credit, DR=debit, null=all
  statuses: EBankTransactionProcessingStatus[]; // Array for multi-select (empty = all)
  search: string; // Search text (empty = no filter)
  bookingRef: string; // Booking reference filter (empty = no filter)
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Derive transaction type from amount sign.
 */
export const getTransactionType = (amount: number): EBankTransactionType => {
  return amount >= 0 ? EBankTransactionType.CREDIT : EBankTransactionType.DEBIT;
};

/**
 * Check if transaction is a credit (positive amount).
 */
export const isCredit = (amount: number): boolean => amount >= 0;

/**
 * Check if transaction is a debit (negative amount).
 */
export const isDebit = (amount: number): boolean => amount < 0;

// ============================================
// IMPORT TYPES
// ============================================

/**
 * Import status enum for bank transaction imports.
 */
export enum EBankTransactionImportStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

/**
 * Cell error within a row during import.
 */
export interface IBankTransactionCellError {
  header: string; // Column name (e.g., "Trx_Date", "Amount", "Currency_Code")
  message: string; // Error message (e.g., "Invalid format", "Unsupported value")
}

/**
 * Row error containing multiple cell errors during import.
 */
export interface IBankTransactionRowError {
  rowNumber: number;
  cellErrors: IBankTransactionCellError[];
}

/**
 * Parse result from the import.
 */
export interface IBankTransactionParseResult {
  fileError: string | null;
  totalRows: number;
  rowErrors: IBankTransactionRowError[];
}

/**
 * User info in import response.
 */
export interface IBankTransactionImportUser {
  uuid: string;
  type: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Full import response from the API.
 */
export interface IBankTransactionImportResponse {
  uuid: string;
  filename: string;
  status: EBankTransactionImportStatus;
  parseResult: IBankTransactionParseResult;
  createdBy: IBankTransactionImportUser;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  url?: string; // Signed URL for downloading the original CSV file (OWA-10233)
}

/**
 * Error response from import API (400).
 */
export interface IBankTransactionImportError {
  errors: Array<{
    message: string;
  }>;
}

// ============================================
// DEPOSIT ACCOUNT TYPES (OWA-10228)
// ============================================

/**
 * Counterparty type enum.
 * TC = Travel Company
 */
export type TCounterpartyType = 'TC';

/**
 * Counterparty for PATCH requests (nullable).
 * Matches BE: CounterpartyNullable
 */
export interface ICounterpartyPatch {
  uuid: string;
  name: string;
  type: TCounterpartyType;
}

/**
 * Allocation entity for PATCH requests.
 * Matches BE: AllocationEntity
 */
export interface IAllocationEntityPatch {
  type: TAllocationEntityType;
  uuid: string;
}

/**
 * Amount allocation for a bank transaction PATCH request.
 * Matches BE: AmountAllocation
 */
export interface IAmountAllocation {
  entity: IAllocationEntityPatch;
  amountCents: number;
  description: string | null;
}

/**
 * Request payload for PATCH /v1/bank-transactions/:uuid
 * Used to save deposit account information.
 * Matches BE: BankTransactionPatchRequest
 */
export interface IBankTransactionPatchRequest {
  counterparty?: ICounterpartyPatch | null;
  exchangeCurrency?: TCurrencyCode | null;
  exchangeRate?: number | null;
  allocations?: IAmountAllocation[];
}

// ============================================
// ALLOCATABLE BOOKINGS TYPES (OWA-10229)
// ============================================

/**
 * Booking available for allocation.
 * Response from POST /api/v1/bank-transactions/bookings-for-allocation
 */
export interface IAllocatableBooking {
  bookingUuid: string;
  humanReadableId: string; // e.g. "PE-12345"
  guestFullName: string;
  arrivalDate: string; // ISO date
  totalSalesCostCents: number;
  outstandingBalanceCents: number;
  status: string; // BookingStatusEnum from BE
}

/**
 * Response from bookings-for-allocation endpoint.
 */
export interface IAllocatableBookingsResponse {
  bookings: IAllocatableBooking[];
}

/**
 * Sort keys supported by BE for allocatable bookings.
 */
export type TAllocatableBookingSortKey =
  | 'arrivalDate'
  | 'guestFullName'
  | 'totalSalesCostCents'
  | 'outstandingBalanceCents'
  | 'status';

/**
 * Request payload for bookings-for-allocation endpoint.
 */
export interface IAllocatableBookingsRequest {
  travelCompanyUuid: string;
  currency: TCurrencyCode;
  sort?: {
    key: TAllocatableBookingSortKey;
    order?: 'asc' | 'desc';
  };
  includeBookingUuids?: string[];
}

/**
 * Local state for tracking allocation to a single booking.
 */
export interface IBookingAllocationState {
  bookingUuid: string;
  humanReadableId: string;
  isSelected: boolean;
  description: string;
  toAllocateCents: number;
}

/**
 * Local state for deposit account allocation.
 */
export interface IDepositAccountAllocationState {
  description: string;
  amountCents: number;
}

// ============================================
// IMPORT LOG TYPES (OWA-10233)
// ============================================

/**
 * Sort keys supported by BE for import log.
 */
export type TImportLogSortKey = 'createdAt' | 'filename' | 'status' | 'rowErrorsCount' | 'createdBy' | 'totalRowsCount';

/**
 * Filter state for import log (local state, not URL).
 */
export interface IImportLogFilters {
  startDate: string | null; // YYYY-MM-DD format
  endDate: string | null; // YYYY-MM-DD format
  statuses: EBankTransactionImportStatus[]; // Empty = All
}

/**
 * API request for import log search.
 * POST /api/v1/bank-transactions/import-log
 */
export interface IImportLogSearchRequest {
  filter?: {
    createdAtFrom?: string; // YYYY-MM-DD format
    createdAtTo?: string; // YYYY-MM-DD format
    statuses?: EBankTransactionImportStatus[];
  };
  sort?: {
    key: TImportLogSortKey;
    order: 'asc' | 'desc';
  };
  pagination?: {
    offset: number;
    limit: number;
  };
}

/**
 * API response for import log search.
 * Matches BE: BankTransactionsImportsSearchResponse
 */
export interface IImportLogSearchResponse {
  imports: IBankTransactionImportResponse[];
  count: number;
  pagination: {
    offset: number;
    limit: number;
  };
}

// ============================================
// NAVIGATION STATE TYPES
// ============================================

/**
 * Location state passed when navigating to receipt/payment detail pages.
 * Used to preserve filter state for back navigation.
 */
export interface IBankTransactionDetailLocationState {
  returnUrl?: string;
}

// ============================================
// EXPORT TYPES (OWA-10235)
// ============================================

/**
 * Export request payload - filter fields go directly in body (not nested).
 * Matches BankTransactionsSearchFilter from BE.
 * Used for POST /api/v1/bank-transactions/export
 *
 * NOTE: Sort is hardcoded on BE (transactionDate descending), not a request param.
 */
export interface IBankTransactionsExportRequest {
  term?: string; // Search narration, reference, counterparty name, amount
  transactionDateFrom?: string; // ISO date string, e.g. "2025-01-01"
  transactionDateTo?: string; // ISO date string
  amountCents?: 'positive' | 'negative'; // CR = positive, DR = negative
  processingStatuses?: EBankTransactionProcessingStatus[];
  paymentMethodGroupCode?: string; // e.g. "BANK_A"
  bookingRef?: string; // Filter by allocated booking humanReadableId
}

/**
 * Trigger endpoint response.
 * Returns exportUuid to poll for completion.
 */
export interface IBankTransactionsExportTriggerResponse {
  exportUuid: string;
}

/**
 * Poll endpoint response (matches BE task-runner.service.ts getDataOnSuccess()).
 * NOTE: Status codes are in the body, NOT HTTP status codes.
 */
export interface IBankTransactionsExportPollResponse {
  message: string;
  status: number; // 200 = complete, 202 = processing, 404 = not found, 422 = failed
  data?: {
    success?: boolean;
    signedUrl?: string; // Present when status is 200
    error?: string; // Present when status is 422
  };
}

/**
 * Status code constants for clarity.
 * These are returned in the body's status field, not HTTP status.
 */
export const EXPORT_STATUS = {
  COMPLETE: 200,
  PROCESSING: 202,
  NOT_FOUND: 404,
  FAILED: 422,
} as const;


export interface IBankTransactionComment {
  /** Format: uuid */
  uuid: string;
  text: string;
  createdBy: {
    uuid: string;
    email: string;
    firstName: string;
    lastName: string;
    type: EUserType;
  };
  /** Format: date-time */
  createdAt: string;
  /** Format: date-time */
  updatedAt: string;
}

export interface BankTransactionsProcessData {
  success?: boolean;
  error?: string;
  bankTransaction?: IBankTransaction;
};

export interface IBankTransactionsProcessResponse {
  message: string;
  status: number;
  data?: BankTransactionsProcessData;
};
